import { lightRequest } from '@/network/request.js'
import { getControlDataInvalidationScopeKey } from '@/common/control-data-invalidation.js'

export const REGION_NAVIGATION_PAGE_SIZE = 50

let activeRegionNavigationRequest = null
let regionNavigationRequestSequence = 0

function createRegionNavigationRequestId() {
  regionNavigationRequestSequence += 1
  return (
    Date.now() +
    ':' +
    regionNavigationRequestSequence +
    ':' +
    Math.random()
      .toString(36)
      .slice(2, 8)
  )
}

export function getRegionNavigationCacheKey(vm) {
  return getControlDataInvalidationScopeKey(vm)
}

export function ensureRegionNavigationScope(vm) {
  const cacheKey = getRegionNavigationCacheKey(vm)
  const state = vm.$store.state.regionnavigation
  if (state.cacheKey !== cacheKey)
    vm.$store.dispatch('asyResetRegionNavigation', { cacheKey: cacheKey })
  return cacheKey
}

/**
 * 请求新版区域索引。reset=true 时固定刷新第一页，并在成功后原子替换缓存；
 * 请求期间继续保留旧数据，同一账号的并发调用复用在途请求，避免切页闪白或重复请求。
 * 分页参数本身就是新旧语义边界，不额外发送模式字段。
 * 若响应没有 page（仅用于兼容异常回退），按老服务端一次性完整结果处理。
 */
export function requestNextRegionNavigationPage(vm, options = {}) {
  const cacheKey = ensureRegionNavigationScope(vm)
  const state = vm.$store.state.regionnavigation
  if (state.loading) {
    if (activeRegionNavigationRequest && activeRegionNavigationRequest.cacheKey === cacheKey) {
      if (options.reset && !activeRegionNavigationRequest.reset) {
        // reset 表示页面明确要求刷新第一页；即使前一个分页请求失败也必须继续，
        // 不能让一次“加载更多”异常吞掉后续的导航刷新。
        return activeRegionNavigationRequest.promise.then(
          () => requestNextRegionNavigationPage(vm, options),
          () => requestNextRegionNavigationPage(vm, options)
        )
      }
      return activeRegionNavigationRequest.promise
    }
    // 热更新等情况下仓库可能保留 loading，但模块中的在途 Promise 已被重建。
    // 页面显示要求刷新第一页时允许再发一个 reset 请求；代次校验会丢弃较晚的旧响应。
    if (!options.reset) return Promise.resolve({ skipped: true })
  }
  if (!options.reset && state.initialized && !state.hasMore)
    return Promise.resolve({ complete: true })

  const current = vm.$store.state.regionnavigation
  const generation = current.generation
  const pageNo = options.reset ? 1 : options.pageNo || current.pageNo + 1
  const pageSize = current.pageSize || REGION_NAVIGATION_PAGE_SIZE
  const requestId = createRegionNavigationRequestId()
  vm.$store.dispatch('asySetRegionNavigationLoading', { loading: true, requestId: requestId })

  let requestPromise
  requestPromise = lightRequest({
    method: 'post',
    url: '/region/findRegion',
    data: {
      token: vm.$store.state.token,
      page: { pageNo: pageNo, pageSize: pageSize }
    }
  })
    .then(res => {
      if (
        generation !== vm.$store.state.regionnavigation.generation ||
        requestId !== vm.$store.state.regionnavigation.loadingRequestId
      )
        return { stale: true }
      if (!res.data || res.data.code != 0)
        return Promise.reject(res.data || { message: '区域请求失败' })
      const responsePage = res.data.page
      const data = res.data.data || {}
      const paged = !!responsePage
      const effectivePageNo = paged ? Number(responsePage.pageNo || pageNo) : 1
      const effectivePageSize = paged
        ? Number(responsePage.pageSize || pageSize)
        : Math.max(1, (data.allRegion || []).length)
      const totalNo = paged ? Number(responsePage.totalNo || 0) : (data.allRegion || []).length
      const hasMore = paged ? effectivePageNo * effectivePageSize < totalNo : false
      vm.$store.dispatch(
        options.reset ? 'asyReplaceRegionNavigationPage' : 'asyMergeRegionNavigationPage',
        {
          cacheKey: cacheKey,
          data: data,
          pageNo: effectivePageNo,
          pageSize: effectivePageSize,
          totalNo: totalNo,
          hasMore: hasMore
        }
      )
      return { data: data, page: responsePage, paged: paged, hasMore: hasMore }
    })
    .finally(() => {
      // replaceRegionNavigationPage 在区域结构变化时会主动提升 generation，因此不能再用
      // 请求前 generation 判断 loading 清理，否则一次真实变更会把 loading 永久留在 true。
      if (cacheKey === vm.$store.state.regionnavigation.cacheKey) {
        vm.$store.dispatch('asySetRegionNavigationLoading', {
          loading: false,
          requestId: requestId
        })
      }
      if (
        activeRegionNavigationRequest &&
        activeRegionNavigationRequest.promise === requestPromise
      ) {
        activeRegionNavigationRequest = null
      }
    })
  activeRegionNavigationRequest = {
    cacheKey: cacheKey,
    reset: options.reset === true,
    promise: requestPromise
  }
  return requestPromise
}

/**
 * Display-time navigation reconciliation. Read only the previously loaded page prefix,
 * then commit once. Never delete an unseen cached region based on an incomplete page.
 * This is separate from explicit selection/reset loading so cached cards are not cleared.
 */
export async function refreshRegionNavigationSnapshot(vm, isCurrent = () => true) {
  const cacheKey = ensureRegionNavigationScope(vm)
  if (activeRegionNavigationRequest) {
    try { await activeRegionNavigationRequest.promise } catch (_) { /* retain cache */ }
  }
  if (!isCurrent() || cacheKey !== ensureRegionNavigationScope(vm)) return { stale: true }
  const state = vm.$store.state.regionnavigation
  const generation = state.generation
  const token = vm.$store.state.token
  const pageCount = Math.max(1, state.pageNo)
  const pageSize = state.pageSize || REGION_NAVIGATION_PAGE_SIZE
  const requestId = createRegionNavigationRequestId()
  vm.$store.dispatch('asySetRegionNavigationLoading', { loading: true, requestId })
  const guard = () => isCurrent() && token === vm.$store.state.token && cacheKey === vm.$store.state.regionnavigation.cacheKey &&
    generation === vm.$store.state.regionnavigation.generation &&
    requestId === vm.$store.state.regionnavigation.loadingRequestId
  const run = async () => {
    const regions = new Map()
    let followRegion = [], navigation = null, totalNo = 0, hasMore = false, pageNo = 0
    for (let page = 1; page <= pageCount; page++) {
      if (!guard()) return { stale: true }
      const res = await lightRequest({ method: 'post', url: '/region/findRegion', data: {
        token: vm.$store.state.token, page: { pageNo: page, pageSize }
      } })
      if (!guard()) return { stale: true }
      if (!res.data || res.data.code != 0) throw new Error('Navigation refresh failed')
      const data = res.data.data || {}, meta = res.data.page
      if (!Array.isArray(data.allRegion)) throw new Error('Invalid navigation snapshot')
      data.allRegion.forEach(region => regions.set(String(region.regionId), region))
      if (page === 1) followRegion = data.followRegion || []
      if (data.navigation) navigation = data.navigation
      pageNo = page
      totalNo = meta ? Number(meta.totalNo || 0) : regions.size
      hasMore = !!meta && page * pageSize < totalNo
      if (!hasMore) break
    }
    if (!guard()) return { stale: true }
    vm.$store.dispatch('asyReconcileRegionNavigationSnapshot', {
      data: { allRegion: [...regions.values()], followRegion, navigation: navigation || state.navigation },
      pageNo, pageSize, totalNo, hasMore
    })
    return { reconciled: true }
  }
  const promise = run().finally(() => {
    if (cacheKey === vm.$store.state.regionnavigation.cacheKey)
      vm.$store.dispatch('asySetRegionNavigationLoading', { loading: false, requestId })
    if (activeRegionNavigationRequest && activeRegionNavigationRequest.promise === promise)
      activeRegionNavigationRequest = null
  })
  activeRegionNavigationRequest = { cacheKey, reset: true, promise }
  return promise
}
